import Breadcrumb from '@components/Common/Breadcrumb';
import ProductGroupCard from '@components/ProductGroupCard';
import MainLayout from '@layouts/MainLayout';
import {useRouter} from 'next/router';

import HeadMeta from '@components/Header/HeadMeta';
import {LocaleEnum} from '@enums/locale';
import {ProductDisplaysEnum} from '@enums/product-display';
import {APIRequestConfig} from '@enums/request-config';
import {ImageInterface} from '@interfaces/images';
import {ProductInterface} from '@interfaces/product';
import {ProductGroupDisplayResponseData} from '@interfaces/product-group-display';
import {isValidURL, slugifyUrl} from '@lib/utils';
import {AxiosResponse} from 'axios';
import {GetStaticPropsContext} from 'next';
import {useTranslation} from 'next-i18next';
import {serverSideTranslations} from 'next-i18next/serverSideTranslations';
import axios from '../../../lib/axios';
import styles from '../style.module.css';
import {rewriteInternalLink} from '@lib/localize-html-links';

export default function Products({
  data,
}: {
  data: Omit<ProductGroupDisplayResponseData, 'tabs'> & {
    products: (ProductInterface & {images: ImageInterface[]})[];
  };
}) {
  const {t} = useTranslation(['product-display']);
  const router = useRouter();
  const locale = router.locale as LocaleEnum;

  const {products, breadcrumb} = data ?? {};
  return router.isFallback ? null : (
    <MainLayout>
      <HeadMeta
        title={
          data.content_container['seo_page_title'] || data['product_group_name']
        }
        description={data.content_container['seo_description']}
        keywords={data.content_container['seo_keywords']}
        canonical_url={data.content_container['canonical_url']}
      />
      <div className="mx-lg-5">
        <Breadcrumb
          borderBottom={true}
          classNames={`${styles.productGroupBreadcrumb}`}
          baseUrl={t('product_base_url')}
          justifyStart={true}
          list={breadcrumb}
        />
      </div>
      <div className="container">
        <div className="row mb-5">
          <h3 className={'my-4'} style={{fontWeight: 600}}>
            {data['product_group_name'].toUpperCase()}
          </h3>
          <div className={'col-md-8'}>
            <div className="row">
              {products.map(product => {
                let product_description = product['product_description'];
                product_description = `${
                  product_description?.split('</p>')[0]
                }</p>`;
                return (
                  <div key={product.id} className="col-sm-6 mb-4">
                    <ProductGroupCard
                      name={product['product_name']}
                      description={product_description}
                      image={product.images[0] || {}}
                      locale={locale}
                      seo_friendly_url={`${slugifyUrl(
                        product['product_name'],
                        locale
                      )}/${product.id}`}
                      type={ProductDisplaysEnum.SINGLE_PRODUCT_DISPLAY}
                    />
                  </div>
                );
              })}
            </div>
          </div>
          <div className="col-md-4">
            <div
              dangerouslySetInnerHTML={{
                __html: data['product_group_description'],
              }}
            ></div>
            <div
              dangerouslySetInnerHTML={{
                __html: data['product_group_information'],
              }}
            ></div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}

export async function getStaticProps({params, locale}: GetStaticPropsContext) {
  try {
    const slug = params!.slug;
    const response: AxiosResponse<{
      data: Omit<ProductGroupDisplayResponseData, 'tabs'> & {
        products: (ProductInterface & {images: ImageInterface[]})[];
      };
    }> = await axios.get<any>(`store/productgroupproductlist/${slug}`, {
      headers: {
        [APIRequestConfig.X_LOCALE]: locale!,
      },
    });

    const data = response.data.data;
    const redirectUrl = data['product_group_redirect_url'];
    if (redirectUrl && isValidURL(redirectUrl)) {
      return {
        redirect: {
          destination: rewriteInternalLink(redirectUrl, locale),
          permanent: false,
        },
      };
    }
    return {
      props: {
        ...(await serverSideTranslations(locale!, [
          'common',
          'product-display',
        ])),
        data: response.data.data,
      },
    };
  } catch (e: any) {
    if (e.response.status < 500) {
      return {notFound: true};
    }
    throw e;
  }
}

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: true,
  };
}
